import { useContext } from 'react';
import { VisuallyHidden } from '@reach/visually-hidden';
import { DialogOverlay, DialogContent } from '@reach/dialog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';

import { layoutContext } from '@context/layout';
import useLayoutOffset from '@hooks/use-layout-offset';

import styles from './Dialog.module.scss';

interface DialogProps {
  show: boolean;
  ariaLabel: string;
  nodeToFocus?: React.RefObject<HTMLElement>;
  onClose?: () => void;
  maxWidth?: `${number}px` | `${number}rem`;
  maxHeight?: `${number}px` | `${number}rem` | `${number}vh`;
  centerOnContent?: boolean;
  /** Show a diplay button allowing to close the dialog */
  showCloseButton?: boolean;
  /** Allow to dismiss the dialog when clicking on the overlay or pressing the escape key */
  allowDismiss?: boolean;
  position?: 'top';
  /** Hid the grey overlay dimming the background */
  hideModalOverlay?: boolean;
}

const Dialog: React.FC<React.PropsWithChildren<DialogProps>> = ({
  children,
  show,
  ariaLabel,
  onClose,
  nodeToFocus,
  maxWidth,
  maxHeight,
  centerOnContent = true,
  showCloseButton = true,
  allowDismiss = true,
  position,
  hideModalOverlay = false,
}) => {
  const { state: navState } = useContext(layoutContext);
  const dialogRef = useLayoutOffset<HTMLDivElement>(show);

  return (
    <DialogOverlay
      isOpen={show}
      onDismiss={() => {
        if (allowDismiss && onClose) {
          onClose();
        }
      }}
      data-hide-modal-overlay={hideModalOverlay}
      className={styles['dialog-overlay']}
      initialFocusRef={nodeToFocus}
    >
      <DialogContent
        className={styles['dialog-content']}
        data-collapsed={navState.collapsed}
        data-center-on-content={centerOnContent}
        data-position={position}
        aria-label={ariaLabel}
        ref={dialogRef}
        style={{
          maxWidth: maxWidth ?? 'fit-content',
          width: maxWidth ? (centerOnContent ? 'calc(94% - var(--nav-offset))' : '94%') : 'auto',
          maxHeight: maxHeight ?? '94%',
          height: maxHeight ? '88%' : 'fit-content',
        }}
      >
        {showCloseButton && onClose ? (
          <button className={styles['close-button']} onClick={onClose}>
            <VisuallyHidden>Close</VisuallyHidden>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        ) : null}
        {children}
      </DialogContent>
    </DialogOverlay>
  );
};

const DialogFooter: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  return <div className={styles['dialog-footer']}>{children}</div>;
};

export default Dialog;
export { DialogFooter };
